import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';
import { API_BASE_URL } from '../constants/config';
import { AuthTokens } from '../types';
import { getSelectedYear } from '../contexts/YearContext';
import { consumeAuditContext } from '../utils/audit-context';
import { invalidateCache } from '../utils/dataCache';
import { queryClient } from '../providers/QueryProvider';
import {
  ACCESS_DENIED_EVENT,
  parseAccessDenial,
} from '../constants/accessDenial';

// Maps a URL resource segment (e.g. 'unit-goals') to the React Query root
// key we use in query key factories (e.g. 'unitGoals'). Anything not in the
// map is passed through verbatim.
const RESOURCE_TO_QUERY_KEY: Record<string, string> = {
  'unit-goals': 'unitGoals',
  'unit-goal-comments': 'unitGoals',
  'sub-processes': 'processes',
  'organizational-units': 'organizationalUnits',
  'goal-comments': 'goals',
  'kpi-comments': 'kpis',
  'initiative-comments': 'initiatives',
  'service-comments': 'services',
};

// Storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Token storage helpers (works on web and native)
const storage = {
  async getItem(key: string): Promise<string | null> {
    if (Platform.OS === 'web') {
      return localStorage.getItem(key);
    }
    return SecureStore.getItemAsync(key);
  },
  async setItem(key: string, value: string): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.setItem(key, value);
      return;
    }
    await SecureStore.setItemAsync(key, value);
  },
  async removeItem(key: string): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.removeItem(key);
      return;
    }
    await SecureStore.deleteItemAsync(key);
  },
};

// Token management
export const tokenManager = {
  async getAccessToken(): Promise<string | null> {
    return storage.getItem(ACCESS_TOKEN_KEY);
  },

  async getRefreshToken(): Promise<string | null> {
    return storage.getItem(REFRESH_TOKEN_KEY);
  },

  async setTokens(tokens: AuthTokens): Promise<void> {
    await storage.setItem(ACCESS_TOKEN_KEY, tokens.access);
    await storage.setItem(REFRESH_TOKEN_KEY, tokens.refresh);
  },

  async clearTokens(): Promise<void> {
    await storage.removeItem(ACCESS_TOKEN_KEY);
    await storage.removeItem(REFRESH_TOKEN_KEY);
  },

  async hasValidToken(): Promise<boolean> {
    const token = await this.getAccessToken();
    return !!token;
  },
};

// Endpoints that should NOT have year filter applied
const YEAR_FILTER_EXCLUDED = ['/auth/', '/users/me/', '/users/change_password/', '/notifications/'];

// Request interceptor - add auth token, year filter, and handle FormData
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = await tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Auto-add year parameter to GET requests (list endpoints).
    // Caller-supplied year (e.g., screens with their own inline year filter)
    // takes precedence — spread the global year FIRST so existing params
    // override it. Reversing this order silently broke per-screen year
    // filters by always overwriting them with the global picker's value.
    if (config.method === 'get') {
      const url = config.url || '';
      const isExcluded = YEAR_FILTER_EXCLUDED.some(ep => url.includes(ep));
      if (!isExcluded) {
        const year = getSelectedYear();
        config.params = { year, ...config.params };
      }
    }

    // Audit context headers
    if (typeof window !== 'undefined') {
      config.headers['X-Audit-Page'] = window.location.pathname;
      const audit = consumeAuditContext();
      if (audit.component) config.headers['X-Audit-Component'] = audit.component;
      if (audit.action) config.headers['X-Audit-Action'] = audit.action;
    }

    // If the data is FormData, delete the default Content-Type header
    // This allows axios to set the correct multipart/form-data header with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Auth endpoints that should NOT trigger token refresh
const AUTH_ENDPOINTS = ['/auth/exchange/', '/auth/refresh/', '/auth/logout/', '/auth/me/'];

// Response interceptor - handle cache invalidation on mutations + token refresh
api.interceptors.response.use(
  (response) => {
    // Invalidate both cache layers on mutations so the next load is fresh:
    //   1. dataCache (home-rolled in-memory + localStorage)
    //   2. React Query (used by Goals, Initiatives, KPIs, UnitGoals, Processes, etc.)
    const method = response.config.method?.toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method || '')) {
      const url = response.config.url || '';
      // Extract resource name: /goals/123/ → goals, /sub-processes/ → sub-processes
      const segments = url.replace(/^\//, '').split('/').filter(Boolean);
      const resource = segments[0];
      if (resource && !resource.startsWith('auth')) {
        // Layer 1: dataCache
        invalidateCache(resource);
        invalidateCache('dashboard'); // Dashboard shows aggregated data

        // Layer 2: React Query — map URL resource to the root query key
        // (most are identity; see RESOURCE_TO_QUERY_KEY for exceptions).
        const queryKeyRoot = RESOURCE_TO_QUERY_KEY[resource] || resource;
        queryClient.invalidateQueries({ queryKey: [queryKeyRoot] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const requestUrl = originalRequest?.url || '';

    // Skip token refresh for auth endpoints
    const isAuthEndpoint = AUTH_ENDPOINTS.some(endpoint => requestUrl.includes(endpoint));

    // If 401 and not already retrying and not an auth endpoint, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        const refreshToken = await tokenManager.getRefreshToken();
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post<AuthTokens>(`${API_BASE_URL}/auth/refresh/`, {
          refresh: refreshToken,
        });

        const { access, refresh } = response.data;
        await tokenManager.setTokens({ access, refresh: refresh || refreshToken });

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to SSO
        await tokenManager.clearTokens();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('auth:session-expired'));
        }
        return Promise.reject(refreshError);
      }
    }

    // Handle SSO access-denial 403s (citizen_blocked, position_type_missing,
    // account_inactive, no_role, no_system_access). These carry a structured
    // `code` in the response body. We clear tokens and emit an event so the
    // AuthContext can render a specific access-denied screen. Do NOT redirect
    // to SSO login here — that produces an infinite loop for blocked users.
    if (error.response?.status === 403) {
      const denial = parseAccessDenial(error.response.data);
      if (denial) {
        await tokenManager.clearTokens();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent(ACCESS_DENIED_EVENT, { detail: denial })
          );
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
